import { useState, useCallback, useMemo, useRef, useEffect, type Ref } from "react"
import { ClipboardCheck, Plus, Loader2, Upload } from "lucide-react"
import { toast } from "sonner"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { Button } from "@/components/ui/button"
import { useStrategyStore } from "@/stores/strategy-store"
import { useStrategySettingsStore } from "@/stores/strategy-settings-store"
import { useDocumentsStore } from "@/stores/documents-store"
import { isRanAndEmpty, useExtractionStore } from "@/stores/extraction-store"
import { useBidsStore } from "@/stores/bids-store"
import { GoNoGoScoreCard } from "@/components/strategy/GoNoGoScoreCard"
import { GoNoGoCategoryGroup } from "@/components/strategy/GoNoGoCategoryGroup"
import { GoNoGoDecisionCard } from "@/components/strategy/GoNoGoDecisionCard"
import { GoNoGoDecisionDialog } from "@/components/strategy/GoNoGoDecisionDialog"
import { GoNoGoAddCriterionDialog } from "@/components/strategy/GoNoGoAddCriterionDialog"
import { AiReadinessBanner } from "@/components/strategy/AiReadinessBanner"
import { CompliancePositionBanner } from "@/components/strategy/CompliancePositionBanner"
import { GoNoGoAiSuggestionBar } from "@/components/strategy/GoNoGoAiSuggestionBar"
import { GoNoGoScoringProgress } from "@/components/strategy/GoNoGoScoringProgress"
import { GoNoGoScoringError } from "@/components/strategy/GoNoGoScoringError"
import { aiSuggestionCount, needsManualReviewCount, pendingAiSuggestionCount } from "@/lib/gng-score"
import { displayStateOf, mandatoryAtRiskItems } from "@/lib/compliance-position"
import type { ExtractedComplianceItem } from "@/types/extraction"
import type { GoNoGoAssessment as Assessment } from "@/stores/strategy-store"
import type { GngCategoryDef } from "@/stores/strategy-settings-store"
import { useNavigate } from "react-router-dom"
import { ViewOnlyNotice } from "@/components/ViewOnlyNotice"

const DEFAULT_CATEGORIES: GngCategoryDef[] = [
  { slug: 'strategic', label: 'Strategic Fit', sortOrder: 1 },
  { slug: 'capability', label: 'Capability', sortOrder: 2 },
  { slug: 'commercial', label: 'Commercial', sortOrder: 3 },
  { slug: 'risk', label: 'Risk', sortOrder: 4 },
  { slug: 'resource', label: 'Resource', sortOrder: 5 },
  { slug: 'relationship', label: 'Relationship', sortOrder: 6 },
]

interface GoNoGoAssessmentProps {
  bidId: string
  /** Marks the hero score card's bottom edge so the parent can toggle the
      sticky strip once the hero scrolls out of view. */
  heroSentinelRef?: Ref<HTMLDivElement>
  /** The reader can't edit this bid (e.g. view-only access): show the assessment, offer no changes. */
  readOnly?: boolean
}

export function GoNoGoAssessment({ bidId, heroSentinelRef, readOnly = false }: GoNoGoAssessmentProps) {
  const navigate = useNavigate()
  const {
    assessment,
    isLoadingAssessment,
    isCreatingAssessment,
    isRecordingDecision,
    isSavingCriterion,
    isAiScoring,
    isApplyingAiScores,
    aiScoringError,
    createAssessment,
    updateCriterion,
    addCriterion,
    removeCriterion,
    recordDecision,
    reopenAssessment,
    triggerAiScoring,
    triggerAiScoringForCriterion,
    acceptAllAiScores,
    refreshAssessment,
  } = useStrategyStore()

  // Load template categories for dynamic rendering
  const settingsTemplate = useStrategySettingsStore(s => s.template)
  const loadTemplate = useStrategySettingsStore(s => s.loadTemplate)
  useEffect(() => {
    if (!settingsTemplate) void loadTemplate()
  }, [settingsTemplate, loadTemplate])
  const categoryDefs = settingsTemplate?.categories ?? DEFAULT_CATEGORIES

  const documents = useDocumentsStore(s => s.documents)
  const hasDocuments = documents.length > 0

  const categories = useExtractionStore(s => s.categories)
  const hasEvalCriteria = categories.evaluation_criteria.items.length > 0
  const complianceItems = categories.compliance_matrix.items as ExtractedComplianceItem[]
  const hasCompliance = complianceItems.length > 0
  const hasKeyDates = categories.key_dates.items.length > 0
  const hasTenderSummary = useBidsStore(s => s.selectedBid?.tenderSummary != null)
  const currentBid = useBidsStore(s => s.selectedBid)
  const refreshBid = useBidsStore(s => s.refreshBid)
  const canAiScore = hasEvalCriteria && hasCompliance

  // Settled-empty: the extraction ran and this pack genuinely defines none. Distinct
  // from never-extracted, which is still outstanding work for the user.
  const criteriaSettledEmpty = isRanAndEmpty(categories.evaluation_criteria)
  const complianceSettledEmpty = isRanAndEmpty(categories.compliance_matrix)
  const keyDatesSettledEmpty = isRanAndEmpty(categories.key_dates)

  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false)

  const complianceRisk = useMemo(() => {
    const { nonComply, needsReview } = mandatoryAtRiskItems(complianceItems)
    return {
      mandatoryNonComplyTitles: nonComply.map((i) => i.title),
      mandatoryNeedsReview: needsReview.length,
      mandatoryNeedsConfirmation: needsReview.filter(
        (i) => displayStateOf(i) === 'needs_confirmation'
      ).length,
    }
  }, [complianceItems])
  const [addCriterionDialogOpen, setAddCriterionDialogOpen] = useState(false)

  // Debounce evidence changes
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  // Each pending timer holds the bid id it was created with, so one left running
  // past unmount saves a criterion on a bid the user has already left. The store
  // now refuses the write, but the request still fires — cancelling here means it
  // never leaves the browser.
  useEffect(() => {
    const timers = debounceTimers.current
    return () => {
      Object.values(timers).forEach(clearTimeout)
    }
  }, [])

  const handleEvidenceChange = useCallback((criterionId: string, evidence: string) => {
    // Optimistic local update
    const { assessment: a } = useStrategyStore.getState()
    if (a) {
      const updatedCriteria = a.criteria.map(c =>
        c.id === criterionId ? { ...c, evidence } : c
      )
      useStrategyStore.setState({ assessment: { ...a, criteria: updatedCriteria } })
    }

    // Debounced API call
    if (debounceTimers.current[criterionId]) {
      clearTimeout(debounceTimers.current[criterionId])
    }
    debounceTimers.current[criterionId] = setTimeout(() => {
      void updateCriterion(bidId, criterionId, { evidence })
    }, 800)
  }, [bidId, updateCriterion])

  const handleScoreChange = useCallback((criterionId: string, score: number | null) => {
    void updateCriterion(bidId, criterionId, { score })
  }, [bidId, updateCriterion])

  const handleWeightChange = useCallback((criterionId: string, weight: number) => {
    void updateCriterion(bidId, criterionId, { weight })
  }, [bidId, updateCriterion])

  const handleRemoveCriterion = useCallback((criterionId: string) => {
    void removeCriterion(bidId, criterionId)
  }, [bidId, removeCriterion])

  const handleAiScoreCriterion = useCallback((criterionId: string) => {
    void triggerAiScoringForCriterion(bidId, criterionId)
  }, [bidId, triggerAiScoringForCriterion])

  const handleApplyAllAiScores = useCallback(async () => {
    if (!await acceptAllAiScores(bidId)) {
      toast.error("Couldn't apply the AI scores — please try again")
    }
  }, [bidId, acceptAllAiScores])

  // Loading state
  if (isLoadingAssessment) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // No assessment yet — show empty state
  if (!assessment) {
    return (
      <div className="py-20 text-center">
        <div className="flex justify-center mb-4">
          <ClipboardCheck className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        </div>
        <h3 className="text-base font-medium">
          {hasDocuments ? 'Ready to assess' : 'Assess this opportunity'}
        </h3>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
          {hasDocuments
            ? 'Score this opportunity against strategic, capability, commercial, and risk criteria. AI will pre-fill scores from your tender analysis.'
            : 'You can start scoring manually now, or upload tender documents first so AI can help.'
          }
        </p>
        {readOnly ? (
          <ViewOnlyNotice className="mt-5 justify-center" />
        ) : (
        <div className="mt-5 flex items-center justify-center gap-3">
          <Button onClick={() => void createAssessment(bidId)} disabled={isCreatingAssessment}>
            {isCreatingAssessment ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</>
            ) : (
              <><ClipboardCheck className="h-4 w-4 mr-2" />Start Assessment</>
            )}
          </Button>
          {!hasDocuments && (
            <Button variant="outline" onClick={() => void navigate(`/app/bids/${bidId}/documents`)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Documents
            </Button>
          )}
        </div>
        )}
      </div>
    )
  }

  // Completed state — show decision card + collapsed criteria
  if (assessment.status === 'completed' && assessment.decision) {
    return (
      <CompletedView
        assessment={assessment}
        onReopen={readOnly ? undefined : () => void reopenAssessment(bidId)}
      />
    )
  }

  // A run is in flight if either the local flag says so OR the fetched
  // assessment says so. Server state is the source of truth: navigating away
  // and back clears the local flag while the worker is still running.
  const isScoringRun = isAiScoring || assessment.aiScoringStatus === 'scoring'

  // In-progress state — scoring UI
  const criteriaByCategory = new Map<string, typeof assessment.criteria>()
  for (const c of assessment.criteria) {
    const existing = criteriaByCategory.get(c.category) ?? []
    existing.push(c)
    criteriaByCategory.set(c.category, existing)
  }

  // Find orphan categories (criteria with categories not in the template)
  const knownSlugs = new Set(categoryDefs.map(c => c.slug))
  const orphanCategories = [...new Set(
    assessment.criteria.map(c => c.category).filter(cat => !knownSlugs.has(cat))
  )]

  return (
    <div className="space-y-6">
      {/* AI readiness banner */}
      <AiReadinessBanner
        bidId={bidId}
        allRequiredDone={canAiScore}
        items={[
          {
            label: 'Evaluation criteria',
            kind: 'evaluation_criteria' as const,
            done: hasEvalCriteria,
            required: true,
            ...(criteriaSettledEmpty ? { settledEmpty: true } : {}),
          },
          {
            label: 'Compliance matrix',
            kind: 'compliance_matrix' as const,
            done: hasCompliance,
            required: true,
            ...(complianceSettledEmpty ? { settledEmpty: true } : {}),
          },
          {
            label: 'Key dates',
            done: hasKeyDates,
            required: false,
            ...(keyDatesSettledEmpty ? { settledEmpty: true } : {}),
          },
          { label: 'Tender summary', done: hasTenderSummary, required: false },
        ]}
      />

      {/* Pre-decision compliance signal (spec §6). In-progress branch only —
          the completed view's post-decision story is the drift nudge. */}
      <CompliancePositionBanner bidId={bidId} items={complianceItems} />

      {/* Bulk accept — hidden mid-run (suggestions are still changing) and once a
          decision exists. Every input is derived from fetched assessment state, so
          navigating away and back cannot lose it. */}
      {(() => {
        const unapplied = pendingAiSuggestionCount(assessment.criteria)
        if (readOnly || unapplied === 0 || isScoringRun || assessment.decision != null) return null
        return (
          <GoNoGoAiSuggestionBar
            suggestedCount={aiSuggestionCount(assessment.criteria)}
            unappliedCount={unapplied}
            totalCount={assessment.criteria.length}
            needsManualCount={needsManualReviewCount(assessment.criteria)}
            isApplying={isApplyingAiScores}
            onApplyAll={() => void handleApplyAllAiScores()}
          />
        )
      })()}

      {/* Score summary */}
      <GoNoGoScoreCard assessment={assessment} categoryDefs={categoryDefs} />
      {/* Marks the hero's bottom edge for the parent's sticky-strip observer. */}
      {heroSentinelRef && <div ref={heroSentinelRef} aria-hidden="true" />}

      {/* Actions bar */}
      <div className="flex items-center justify-between gap-2">
        {/* A live run's progress readout wins: a stale error must never sit
            beside a running spinner. */}
        <div className="min-w-0">
          {isScoringRun ? (
            <GoNoGoScoringProgress
              stage={assessment.aiScoringStage}
              startedAt={assessment.aiScoringStartedAt}
              criteriaCount={assessment.criteria.length}
            />
          ) : aiScoringError ? (
            <GoNoGoScoringError
              kind={aiScoringError.kind}
              message={aiScoringError.message}
              onRetry={() => void triggerAiScoring(bidId)}
              onRefresh={() => void refreshAssessment(bidId)}
            />
          ) : null}
        </div>
        {!readOnly && (
        <div className="flex items-center gap-2">
          {canAiScore && (() => {
            const hasAnyAiScore = assessment.criteria.some(c => c.aiSuggestedScore != null || c.aiRationale != null)
            const label = isAiScoring ? 'AI Scoring...' : hasAnyAiScore ? 'Re-score all with AI' : 'AI Pre-Score all'
            // `still_running` means we stopped watching, not that the run died.
            // Leaving this enabled would offer exactly the action the notice
            // withholds — and start a second ~$0.20 run against a live one.
            const mayStillBeRunning = aiScoringError?.kind === 'still_running'
            return (
              <Button
                variant="ai-ghost"
                size="sm"
                onClick={() => void triggerAiScoring(bidId)}
                disabled={isAiScoring || mayStillBeRunning}
              >
                {isAiScoring
                  ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <GradientSparkleIcon className="h-3.5 w-3.5 mr-1.5" />}
                {label}
              </Button>
            )
          })()}
          <Button variant="ghost" size="sm" onClick={() => setAddCriterionDialogOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Criterion
          </Button>
        </div>
        )}
      </div>

      {/* Category groups */}
      {categoryDefs.map(catDef => {
        const criteria = criteriaByCategory.get(catDef.slug)
        if (!criteria || criteria.length === 0) return null
        return (
          <GoNoGoCategoryGroup
            key={catDef.slug}
            categoryLabel={catDef.label}
            categorySlug={catDef.slug}
            criteria={criteria}
            onScoreChange={handleScoreChange}
            onEvidenceChange={handleEvidenceChange}
            onWeightChange={handleWeightChange}
            onRemove={handleRemoveCriterion}
            onAiScoreCriterion={canAiScore && !readOnly ? handleAiScoreCriterion : undefined}
            readOnly={readOnly}
            canEditStructure={!readOnly}
            scoringSources={assessment.aiScoringSources}
          />
        )
      })}

      {/* Orphan categories (from deleted template categories) */}
      {orphanCategories.map(slug => {
        const criteria = criteriaByCategory.get(slug)
        if (!criteria || criteria.length === 0) return null
        return (
          <GoNoGoCategoryGroup
            key={slug}
            categoryLabel={slug.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            categorySlug={slug}
            criteria={criteria}
            onScoreChange={handleScoreChange}
            onEvidenceChange={handleEvidenceChange}
            onWeightChange={handleWeightChange}
            onRemove={handleRemoveCriterion}
            onAiScoreCriterion={canAiScore && !readOnly ? handleAiScoreCriterion : undefined}
            readOnly={readOnly}
            canEditStructure={!readOnly}
            scoringSources={assessment.aiScoringSources}
          />
        )
      })}

      {/* Decision bar */}
      <div className="flex items-center justify-between pt-2">
        <span className="text-sm text-muted-foreground">
          {assessment.criteria.filter(c => c.score != null).length} of {assessment.criteria.length} criteria scored
        </span>
        {!readOnly && (
          <Button onClick={() => setDecisionDialogOpen(true)}>
            Record Decision
          </Button>
        )}
      </div>

      {/* Dialogs */}
      <GoNoGoDecisionDialog
        open={decisionDialogOpen}
        onOpenChange={setDecisionDialogOpen}
        assessment={assessment}
        currentBid={currentBid}
        isLoading={isRecordingDecision}
        compliance={complianceRisk}
        onConfirm={(data) => {
          void recordDecision(bidId, data).then(() => {
            setDecisionDialogOpen(false)
            // The decision may have moved the bid: re-read it for the header.
            if (data.transitionBidStatus) void refreshBid(bidId)
          })
        }}
      />

      <GoNoGoAddCriterionDialog
        open={addCriterionDialogOpen}
        onOpenChange={setAddCriterionDialogOpen}
        isLoading={isSavingCriterion}
        categories={categoryDefs.map(c => ({ value: c.slug, label: c.label }))}
        onConfirm={(data) => {
          void addCriterion(bidId, data).then(() => setAddCriterionDialogOpen(false))
        }}
      />
    </div>
  )
}

// ===================
// Completed View (read-only)
// ===================

function CompletedView({ assessment, onReopen }: { assessment: Assessment; onReopen: (() => void) | undefined }) {
  const settingsTemplate = useStrategySettingsStore(s => s.template)
  const categoryDefs = settingsTemplate?.categories ?? DEFAULT_CATEGORIES

  const criteriaByCategory = new Map<string, typeof assessment.criteria>()
  for (const c of assessment.criteria) {
    const existing = criteriaByCategory.get(c.category) ?? []
    existing.push(c)
    criteriaByCategory.set(c.category, existing)
  }

  const knownSlugs = new Set(categoryDefs.map(c => c.slug))
  const orphanCategories = [...new Set(
    assessment.criteria.map(c => c.category).filter(cat => !knownSlugs.has(cat))
  )]

  return (
    <div className="space-y-6">
      <GoNoGoDecisionCard
        assessment={assessment}
        onReopen={onReopen}
      />

      {/* Collapsed criteria (read-only) */}
      {categoryDefs.map(catDef => {
        const criteria = criteriaByCategory.get(catDef.slug)
        if (!criteria || criteria.length === 0) return null
        return (
          <GoNoGoCategoryGroup
            key={catDef.slug}
            categoryLabel={catDef.label}
            categorySlug={catDef.slug}
            criteria={criteria}
            onScoreChange={() => {}}
            onEvidenceChange={() => {}}
            readOnly={true}
            scoringSources={assessment.aiScoringSources}
          />
        )
      })}

      {/* Orphan categories */}
      {orphanCategories.map(slug => {
        const criteria = criteriaByCategory.get(slug)
        if (!criteria || criteria.length === 0) return null
        return (
          <GoNoGoCategoryGroup
            key={slug}
            categoryLabel={slug.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            categorySlug={slug}
            criteria={criteria}
            onScoreChange={() => {}}
            onEvidenceChange={() => {}}
            readOnly={true}
            scoringSources={assessment.aiScoringSources}
          />
        )
      })}
    </div>
  )
}
